/**
 * StatusBreakdownChips
 *
 * Inline status-breakdown row used on every Reports drill-down panel
 * (Location, Job Type, Referral, Priority, Customer Type).
 *
 * Shows count + revenue per status for the supplied jobs, with the chip
 * coloured to match the user's configured status colour. Clicking a chip
 * toggles a status filter via the provided onChange callback.
 *
 * Props:
 *   jobs         — array of job records already scoped to the selected category
 *   statusColors — settings.status_colors map { 'Booked': '#3b82f6', ... }
 *   value        — currently selected status string or null
 *   onChange     — (statusName|null) => void
 *   label        — short label shown in the panel header (e.g. 'NSW', 'Apple')
 *   revenueField — 'total_amount' (invoiced, default) or 'revenue_amount'
 *                  (cash-basis). Use 'revenue_amount' on drill-ins whose
 *                  parent table is cash-basis (Referral Sources, Cash by
 *                  Source) so the numbers reconcile end-to-end. Feb 2026 —
 *                  user reported: Right box (cash) showed Google $6015 but
 *                  drill chip Finish showed $8615 (invoiced) — mismatch
 *                  because the two were on different bases.
 */
export const StatusBreakdownChips = ({
  jobs = [],
  statusColors = {},
  value = null,
  onChange,
  label = '',
  revenueField = 'total_amount',
}) => {
  const basisLabel = revenueField === 'revenue_amount' ? 'cash received' : 'invoiced total';
  const breakdown = {};
  jobs.forEach((j) => {
    const s = j.status || 'Unknown';
    if (!breakdown[s]) breakdown[s] = { count: 0, revenue: 0 };
    breakdown[s].count += 1;
    breakdown[s].revenue += parseFloat(j[revenueField] || 0);
  });
  const entries = Object.entries(breakdown).sort((a, b) => b[1].count - a[1].count);
  const total = jobs.reduce((sum, j) => sum + parseFloat(j[revenueField] || 0), 0);

  const chipStyle = (active, color) => ({
    padding: '6px 12px',
    borderRadius: '999px',
    border: '1px solid',
    borderColor: active ? color : '#cbd5e1',
    background: active ? color : '#ffffff',
    color: active ? '#ffffff' : '#0f172a',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
  });

  return (
    <div
      style={{
        marginBottom: '1.25rem',
        padding: '12px 14px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
      }}
      data-testid="status-breakdown-chips"
    >
      <div
        style={{
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.6rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span>Status breakdown{label ? ` for ${label}` : ''}</span>
        <span
          style={{ textTransform: 'none', fontSize: '0.7rem', color: '#94a3b8', fontWeight: 400, letterSpacing: 0 }}
          title={
            revenueField === 'revenue_amount'
              ? 'Chip $ shows CASH RECEIVED only (payments matched to paid jobs). Reconciles with cash-basis tabs (Referral Sources, P&L).'
              : 'Chip $ shows INVOICED total (may include unpaid work). For cash-received view, see the Cash by Source tab.'
          }
        >
          $ = {basisLabel}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="status-chip-all"
          style={chipStyle(value === null, '#0f172a')}
        >
          All {jobs.length} · ${total.toFixed(2)}
        </button>
        {entries.map(([statusName, info]) => {
          const active = value === statusName;
          const color = statusColors?.[statusName] || '#64748b';
          return (
            <button
              key={statusName}
              type="button"
              onClick={() => onChange(active ? null : statusName)}
              data-testid={`status-chip-${statusName.toLowerCase().replace(/\s+/g, '-')}`}
              style={chipStyle(active, color)}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: active ? '#ffffff' : color,
                }}
              />
              {statusName} {info.count} · ${info.revenue.toFixed(2)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StatusBreakdownChips;
